import React, { useCallback, useEffect, useState } from 'react';
import {
  stockPortfolioService,
  StockActivityBot,
} from '../../services/stockPortfolioService';
import StockActivityBotCreateDrawer from './StockActivityBotCreateDrawer';

const POLL_MS = 120_000;

interface StockActivityBotPanelProps {
  canManage: boolean;
  cardStyle: React.CSSProperties;
  selectedAccountId?: number | null;
  challengeBotIds?: number[];
  onPortfolioRefresh?: () => void;
}

const StockActivityBotPanel: React.FC<StockActivityBotPanelProps> = ({
  canManage,
  cardStyle,
  selectedAccountId,
  challengeBotIds = [],
  onPortfolioRefresh,
}) => {
  const [bots, setBots] = useState<StockActivityBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedBotId, setExpandedBotId] = useState<number | null>(null);

  const challengeSet = new Set(challengeBotIds);

  const load = useCallback(async () => {
    try {
      const botList = await stockPortfolioService.listStockActivityBots();
      setBots(botList);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load bots');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const runAction = async (botId: number, action: () => Promise<StockActivityBot>) => {
    setBusyId(botId);
    try {
      await action();
      await load();
      onPortfolioRefresh?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const standaloneBots = bots.filter(b => !b.challengeBot && !challengeSet.has(b.id));

  return (
    <>
      <div style={{ ...cardStyle, marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Stock Activity Bots</div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
              Long-only automation — MI-gated entries, STOP on buy, polled TP / macro filter exits (120s tick)
            </div>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: 'none',
                background: '#086afb',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              + New bot
            </button>
          )}
        </div>

        {error && !drawerOpen && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 12 }}>
            {error}
          </div>
        )}

        {loading ? (
          <p style={{ margin: '14px 0 0', fontSize: 13, color: '#94a3b8' }}>Loading bots…</p>
        ) : bots.length === 0 ? (
          <p style={{ margin: '14px 0 0', fontSize: 13, color: '#64748b' }}>
            No activity bots yet. Challenge-owned bots appear under Stock Growth Challenge above.
          </p>
        ) : (
          <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
            {bots.map(bot => {
              const busy = busyId === bot.id;
              const isChallenge = bot.challengeBot || challengeSet.has(bot.id);
              const expanded = expandedBotId === bot.id;
              return (
                <div key={bot.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '10px 12px', background: '#f8fafc' }}>
                    <button
                      type="button"
                      onClick={() => setExpandedBotId(expanded ? null : bot.id)}
                      style={{ border: 'none', background: 'transparent', fontWeight: 700, color: '#1e293b', cursor: 'pointer', fontSize: 13, padding: 0 }}
                    >
                      {expanded ? '▼' : '▶'} {bot.referenceName || `Bot #${bot.id}`}
                    </button>
                    {isChallenge && (
                      <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8' }}>Challenge</span>
                    )}
                    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: bot.status === 'RUNNING' ? '#dcfce7' : '#f1f5f9', color: bot.status === 'RUNNING' ? '#15803d' : '#64748b' }}>
                      {bot.status}
                    </span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>${Number(bot.amount).toFixed(2)}/trade</span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{bot.openLegs ?? 0} open</span>
                    {canManage && !isChallenge && (
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        {bot.status !== 'RUNNING' && (
                          <button type="button" disabled={busy} onClick={() => void runAction(bot.id, () => stockPortfolioService.startStockActivityBot(bot.id))} style={{ padding: '4px 10px', borderRadius: 999, border: 'none', background: '#086afb', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Start</button>
                        )}
                        {bot.status === 'RUNNING' && (
                          <button type="button" disabled={busy} onClick={() => void runAction(bot.id, () => stockPortfolioService.pauseStockActivityBot(bot.id))} style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid #cbd5e1', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Pause</button>
                        )}
                      </span>
                    )}
                  </div>
                  {bot.lastError && (
                    <div style={{ padding: '6px 12px', fontSize: 11, color: '#b45309', borderTop: '1px solid #e2e8f0' }}>{bot.lastError}</div>
                  )}
                  {expanded && (bot.legs?.length ?? 0) > 0 && (
                    <div style={{ padding: '8px 12px', borderTop: '1px solid #e2e8f0' }}>
                      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ color: '#64748b', textAlign: 'left' }}>
                            <th style={{ padding: '4px 6px' }}>Symbol</th>
                            <th style={{ padding: '4px 6px' }}>Status</th>
                            <th style={{ padding: '4px 6px' }}>Entry</th>
                            <th style={{ padding: '4px 6px' }}>Stop</th>
                            <th style={{ padding: '4px 6px' }}>P&L</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bot.legs!.map(leg => (
                            <tr key={leg.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '4px 6px' }}>{leg.displaySymbol || leg.ticker}</td>
                              <td style={{ padding: '4px 6px' }}>{leg.status}{leg.exitReason ? ` (${leg.exitReason})` : ''}</td>
                              <td style={{ padding: '4px 6px' }}>{leg.entryPrice != null ? `$${leg.entryPrice.toFixed(2)}` : '—'}</td>
                              <td style={{ padding: '4px 6px' }}>{leg.stopPrice != null ? `$${leg.stopPrice.toFixed(2)}` : '—'}</td>
                              <td style={{ padding: '4px 6px', color: Number(leg.pnl ?? 0) >= 0 ? '#15803d' : '#b91c1c' }}>
                                {leg.pnl != null ? `$${Number(leg.pnl).toFixed(2)}` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {standaloneBots.length === 0 && bots.some(b => b.challengeBot || challengeSet.has(b.id)) && (
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#94a3b8' }}>All bots are owned by an active challenge.</p>
        )}
      </div>

      <StockActivityBotCreateDrawer
        open={drawerOpen && canManage}
        onClose={() => setDrawerOpen(false)}
        defaultAccountId={selectedAccountId}
        onSaved={() => void load()}
      />
    </>
  );
};

export default StockActivityBotPanel;
